import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Icon } from "@wato/ui";
import { useApi } from "../../api/context";
import type { FlowErrorList } from "../../api/types";
import { mayUse } from "../roles/role-label";
import { useWorkspace } from "../workspace/WorkspaceContext";

const LATEST_SHOWN = 3;

/** OWN-283: «⚠ أخطاء تحتاج انتباهك (n)» — latest unresolved flow errors; hidden when there are none. */
export function HomeFlowErrors({ workspaceId }: { readonly workspaceId: string }) {
  const { t } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const allowed = mayUse(workspace, "flows.view");
  const [data, setData] = useState<FlowErrorList | null>(null);
  useEffect(() => {
    if (!allowed) return;
    let live = true;
    api.listFlowErrors(workspaceId, { status: "unresolved" }).then(
      (r) => live && setData(r),
      () => live && setData(null),
    );
    return () => {
      live = false;
    };
  }, [api, workspaceId, allowed]);
  if (!allowed || !data || data.unresolved === 0) return null;
  return (
    <section
      aria-label={t("flows.b29errors.homeTitle", { count: data.unresolved })}
      data-testid="home-flow-errors"
      className="flex flex-col gap-2 rounded-2xl border border-app-line bg-app-surface p-5"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="m-0 flex items-center gap-2 text-base font-semibold">
          <Icon name="warning" className="size-5 text-danger-fg" />
          {t("flows.b29errors.homeTitle", { count: data.unresolved })}
        </h2>
        <Link to={`/w/${workspaceId}/automations/errors`} className="text-sm text-accent">
          {t("flows.b29errors.viewAll")}
        </Link>
      </div>
      <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
        {data.items.slice(0, LATEST_SHOWN).map((g) => (
          <li key={g.key} className="text-fg-muted">
            <span className="text-fg">{t(`flows.b29errors.reason.${g.code}`)}</span> · {g.flowName}
          </li>
        ))}
      </ul>
    </section>
  );
}
